/**
 * Admin Settings Service
 * Handles CRUD operations for application settings management (SystemAdmin only)
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface ApplicationSettingResponse {
  id: string;
  key: string;
  value: string;
  longValue: string | null;
  valueType: string;
  category: string;
  description: string | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateApplicationSettingRequest {
  value: string;
  longValue?: string | null;
  description?: string | null;
  isPublic: boolean;
}

export interface CreateApplicationSettingRequest {
  key: string;
  value: string;
  longValue?: string | null;
  valueType: string;
  category: string;
  description?: string | null;
  isPublic: boolean;
}

/**
 * List all application settings
 */
export const listApplicationSettings = async (): Promise<ApplicationSettingResponse[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ADMIN_SETTINGS.LIST);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Update an existing application setting
 */
export const updateApplicationSetting = async (
  id: string,
  data: UpdateApplicationSettingRequest
): Promise<ApplicationSettingResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ADMIN_SETTINGS.UPDATE(id), {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Create a new application setting
 */
export const createApplicationSetting = async (
  data: CreateApplicationSettingRequest
): Promise<ApplicationSettingResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ADMIN_SETTINGS.CREATE, {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
